
import React, { useEffect, useState, useRef } from 'react';
import * as Plot from '@observablehq/plot';
import { apiClient } from '../utils/api';
import ErrorMessage from '../components/common/ErrorMessage';
import { useAuth } from '../auth/AuthContext';
import { VIEWS } from '../utils/constants';

export function AppMetrics() {
    const [metrics, setMetrics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [view, setView] = useState(VIEWS.PLOTS);

    const { user, hasPermission } = useAuth();
    const isAdmin = hasPermission('users:read');

    const plotRef = useRef(null);

    // Fetch users for dropdown if admin
    useEffect(() => {
        if (isAdmin) {
            apiClient.getUsers()
                .then(setUsers)
                .catch(err => console.error("Failed to load users", err));
        }
    }, [isAdmin]);

    // Fetch metrics
    useEffect(() => {
        const fetchMetrics = async () => {
            setLoading(true);
            setError(null);
            try {
                let data;
                if (selectedUserId) {
                    data = await apiClient.getUserWlanAppMetrics(selectedUserId);
                } else if (isAdmin) {
                    data = await apiClient.getAllWlanAppMetrics();
                } else if (user?.id) {
                    data = await apiClient.getUserWlanAppMetrics(user.id);
                } else {
                    data = [];
                }

                // Parse dates
                const parsed = data.map(d => ({
                    ...d,
                    date: new Date(d.logged_at || d.created_at)
                }));

                setMetrics(parsed);
            } catch (err) {
                setError(err.message || 'Failed to load metrics');
            } finally {
                setLoading(false);
            }
        };

        fetchMetrics();
    }, [isAdmin, selectedUserId, user?.id]);

    // Render Plot
    useEffect(() => {
        if (view !== VIEWS.PLOTS || !metrics.length || !plotRef.current) return;

        // Clear previous plot
        plotRef.current.innerHTML = '';

        const plot = Plot.plot({
            style: { background: "transparent", fontSize: "12px", fontFamily: "system-ui, sans-serif" },
            marginLeft: 60,
            grid: true,
            x: { label: "Time", type: "time" },
            y: { label: "Speed (Mbps)" },
            color: { legend: true },
            marks: [
                Plot.lineY(metrics, { x: "date", y: "link_speed", stroke: "#2196f3", tip: true, title: d => `Link Speed: ${d.link_speed} Mbps` }),
                Plot.lineY(metrics, { x: "date", y: "rx_link_speed_mbps", stroke: "#4caf50", tip: true, title: d => `RX Speed: ${d.rx_link_speed_mbps} Mbps` }),
                Plot.lineY(metrics, { x: "date", y: "tx_link_speed_mbps", stroke: "#ff9800", tip: true, title: d => `TX Speed: ${d.tx_link_speed_mbps} Mbps` }),
                Plot.lineY(metrics, { x: "date", y: "avg_speed_mbps", stroke: "#f44336", tip: true, title: d => `Avg Speed: ${d.avg_speed_mbps} Mbps` }),
                Plot.ruleY([0])
            ]
        });

        plotRef.current.append(plot);

        return () => plot.remove();
    }, [metrics, view]);

    return (
        <div className="dashboard-content">
            <header style={{ marginBottom: '20px' }}>
                <h2>App Metrics</h2>
                <p className="section-intro">
                    View WLAN application metrics analysis.
                </p>
            </header>

            <section className="data-controls">
                <div className="data-controls__layout">
                    {isAdmin && (
                        <div className="data-controls__device">
                            <div className="data-controls__title">Select User</div>
                            <select
                                className="device-select"
                                value={selectedUserId}
                                onChange={e => setSelectedUserId(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '200px' }}
                            >
                                <option value="">All Users</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.firstname ? `${u.firstname} ${u.lastname || ''}` : u.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
                <div className="data-controls__actions">
                    <div className="view-buttons">
                        <button
                            onClick={() => setView(VIEWS.PLOTS)}
                            className={`view-button ${view === VIEWS.PLOTS ? 'active' : ''}`}
                        >
                            📊 Charts
                        </button>
                        <button
                            onClick={() => setView(VIEWS.TABLES)}
                            className={`view-button ${view === VIEWS.TABLES ? 'active' : ''}`}
                        >
                            📋 Tables
                        </button>
                    </div>
                </div>
            </section>

            <hr />

            {loading && <div className="loading-message">Loading Metrics...</div>}
            {error && <ErrorMessage title="Error" message={error} />}

            {!loading && !error && metrics.length > 0 && (
                <div className="data-section">
                    {view === VIEWS.PLOTS ? (
                        <div ref={plotRef} style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
                    ) : (
                        <div className="table-container" style={{ overflowX: 'auto' }}>
                            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                                <thead>
                                    <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                                        <th style={{ padding: '10px' }}>Time</th>
                                        <th style={{ padding: '10px' }}>User ID</th>
                                        <th style={{ padding: '10px' }}>SSID</th>
                                        <th style={{ padding: '10px' }}>BSSID</th>
                                        <th style={{ padding: '10px' }}>Link Speed</th>
                                        <th style={{ padding: '10px' }}>RX (Mbps)</th>
                                        <th style={{ padding: '10px' }}>TX (Mbps)</th>
                                        <th style={{ padding: '10px' }}>Avg (Mbps)</th>
                                        <th style={{ padding: '10px' }}>RSSI</th>
                                        <th style={{ padding: '10px' }}>Location ID</th>
                                        <th style={{ padding: '10px' }}>Bandwidth</th>
                                        <th style={{ padding: '10px' }}>Frequency</th>
                                        <th style={{ padding: '10px' }}>WiFi Standard</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {metrics.map((m, i) => (
                                        <tr key={m.id || i} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '10px' }}>{m.date.toLocaleString()}</td>
                                            <td style={{ padding: '10px' }}>{m.user_id}</td>
                                            <td style={{ padding: '10px' }}>{m.ssid}</td>
                                            <td style={{ padding: '10px' }}>{m.bssid}</td>
                                            <td style={{ padding: '10px' }}>{m.link_speed}</td>
                                            <td style={{ padding: '10px' }}>{m.rx_link_speed_mbps}</td>
                                            <td style={{ padding: '10px' }}>{m.tx_link_speed_mbps}</td>
                                            <td style={{ padding: '10px' }}>{m.avg_speed_mbps}</td>
                                            <td style={{ padding: '10px' }}>{m.rssi}</td>
                                            <td style={{ padding: '10px' }}>{m.location_id}</td>
                                            <td style={{ padding: '10px' }}>{m.bandwidth}</td>
                                            <td style={{ padding: '10px' }}>{m.frequency}</td>
                                            <td style={{ padding: '10px' }}>{m.wifi_standard}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
            {!loading && !error && metrics.length === 0 && (
                <div className="empty-state" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>No metrics found.</div>
            )}
        </div>
    );
}
